import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { preferencesApi } from '../services/preferencesApi';
import { PracticeArea, isValidPracticeArea } from '../types/practiceArea';

export type UserMode = 'lawyer' | 'judge' | 'layperson';

// localStorage key for the practice-area lens — lets first paint gate the
// sidebar before the prefs API resolves (design §2.3).
const LENS_KEY = 'nyayabodh.activePracticeArea';

interface UserPreferences {
  id: string;
  user_id: string;
  user_mode: UserMode;
  language: string;
  theme: 'light' | 'dark' | 'system';
  font_size: string;
  simplified_view: boolean;
  auto_translate: boolean;
  high_contrast: boolean;
  reduce_motion: boolean;
  [key: string]: any;
}

interface PreferencesContextType {
  preferences: UserPreferences | null;
  loading: boolean;
  error: Error | null;
  userMode: UserMode;
  // Practice-area workspace lens (null = "All areas")
  activePracticeArea: PracticeArea | null;
  setActivePracticeArea: (pa: PracticeArea | null) => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  setUserMode: (mode: UserMode) => Promise<void>;
  reload: () => Promise<void>;
}

function readLensFromStorage(): PracticeArea | null {
  try {
    const v = localStorage.getItem(LENS_KEY);
    return v && isValidPracticeArea(v) ? v : null;
  } catch {
    return null;
  }
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Hydrate the lens from localStorage for instant first-paint gating.
  const [activePracticeArea, setLensState] = useState<PracticeArea | null>(readLensFromStorage);

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const prefs = await preferencesApi.getPreferences();
      setPreferences(prefs);
      // Server value is authoritative once loaded; keep storage in sync.
      const serverLens = isValidPracticeArea(prefs.active_practice_area)
        ? (prefs.active_practice_area as PracticeArea)
        : null;
      setLensState(serverLens);
      try {
        if (serverLens) localStorage.setItem(LENS_KEY, serverLens);
        else localStorage.removeItem(LENS_KEY);
      } catch {
        /* ignore storage errors */
      }
      setError(null);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    try {
      const updated = await preferencesApi.updatePreferences(updates);
      setPreferences(updated);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const setUserMode = async (mode: UserMode) => {
    await updatePreferences({ user_mode: mode });
  };

  const setActivePracticeArea = async (pa: PracticeArea | null) => {
    // Optimistic: update local state + storage immediately so the sidebar
    // re-gates without waiting on the network.
    setLensState(pa);
    try {
      if (pa) localStorage.setItem(LENS_KEY, pa);
      else localStorage.removeItem(LENS_KEY);
    } catch {
      /* ignore */
    }
    await updatePreferences({ active_practice_area: pa });
  };

  const userMode: UserMode = preferences?.user_mode || 'lawyer';

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        error,
        userMode,
        activePracticeArea,
        setActivePracticeArea,
        updatePreferences,
        setUserMode,
        reload: loadPreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

export const useUserMode = (): UserMode => {
  const { userMode } = usePreferences();
  return userMode;
};

export const useActivePracticeArea = (): PracticeArea | null => {
  const { activePracticeArea } = usePreferences();
  return activePracticeArea;
};
